<script setup>
import { useApi } from '@/utils/api'
import { useAbility } from '@casl/vue'

definePage({
  meta: {
    layout: 'default',
    action: 'read',
    subject: 'User',
  },
})

const ability = useAbility()

// ── State: List ───────────────────────────────────────────────────────────────
const users = ref([])
const roles = ref([])
const loading = ref(false) 
const totalItems = ref(0)
const page = ref(1)
const perPage = ref(15)

// Filters
const search = ref('')
const filterRole = ref(null)
const filterActive = ref(null)

const activeOptions = [
  { title: 'Semua Status', value: null },
  { title: 'Aktif', value: 1 },
  { title: 'Nonaktif', value: 0 },
]

// ── State: Create/Edit Dialog ────────────────────────────────────────────────
const dialog = ref(false)
const dialogTitle = ref('')
const editingId = ref(null)
const saving = ref(false)
const errors = ref({})
const showPassword = ref(false)

const formDefault = () => ({
  name: '',
  email: '',
  phone: '',
  password: '',
  role: '',
  is_active: true,
})
const form = ref(formDefault())

// ── State: Delete ─────────────────────────────────────────────────────────────
const deleteDialog = ref(false)
const deletingUser = ref(null)
const deleting = ref(false)

// ── State: Toggle Active ──────────────────────────────────────────────────────
const togglingId = ref(null)

// ── State: Detail Absensi (PIN & QR, Addendum 08 patch) ──────────────────────
const attendanceDialog = ref(false)
const attendanceUser = ref(null)

const openAttendanceDetail = user => {
  attendanceUser.value = user
  attendanceDialog.value = true
}

// ── Fetch — ✅ FIX: Sama seperti Category ─────────────────────────────────────
const fetchUsers = async () => {
  loading.value = true
  try {
    const query = { page: page.value, per_page: perPage.value }
    if (search.value) query.search = search.value
    if (filterRole.value) query.role = filterRole.value
    if (filterActive.value !== null) query.is_active = filterActive.value

    const response = await useApi('/api/v1/users', { query }).get()
    const data = response?.data ?? response

    // ✅ PARSING YANG BENAR (sama seperti Category)
    users.value = Array.isArray(data) ? data : (data?.data ?? [])
    totalItems.value = data?.meta?.total ?? data?.total ?? 0
  } catch (e) {
    console.error('Fetch users error:', e)
  } finally {
    loading.value = false
  }
}

const fetchRoles = async () => {
  try {
    const response = await useApi('/api/v1/roles').get()
    const data = response?.data ?? response
    roles.value = Array.isArray(data) ? data : (data?.data ?? [])
  } catch (e) {
    console.error('Fetch roles error:', e)
  }
}

onMounted(() => {
  fetchUsers()
  fetchRoles()
})

watch([search, filterRole, filterActive], () => {
  page.value = 1
  fetchUsers()
})
watch(page, fetchUsers)

// ── Dialog Handlers ───────────────────────────────────────────────────────────
const openCreate = () => {
  editingId.value = null
  form.value = formDefault()
  errors.value = {}
  showPassword.value = false
  dialogTitle.value = 'Tambah User'
  dialog.value = true
}

const openEdit = (user) => {
  editingId.value = user.id
  form.value = {
    name: user.name,
    email: user.email,
    phone: user.phone ?? '',
    password: '',
    role: user.roles?.[0] ?? '',
    is_active: user.is_active,
  }
  errors.value = {}
  showPassword.value = false
  dialogTitle.value = 'Edit User'
  dialog.value = true
}

const save = async () => {
  saving.value = true
  errors.value = {}
  try {
    const payload = { ...form.value }
    if (editingId.value && !payload.password) delete payload.password

    const api = useApi(
      editingId.value ? `/api/v1/users/${editingId.value}` : '/api/v1/users',
    )
    const response = editingId.value
      ? await api.put({ body: payload })
      : await api.post({ body: payload })

    const saved = response?.data ?? response
    if (editingId.value) {
      const idx = users.value.findIndex(u => u.id === editingId.value)
      if (idx !== -1) users.value[idx] = saved
    } else {
      fetchUsers()
    }
    dialog.value = false
  } catch (e) {
    if (e?.status === 422) {
      errors.value = e?.data?.errors ?? {}
    } else {
      console.error('Save user error:', e)
    }
  } finally {
    saving.value = false
  }
}

// ── Toggle Active ─────────────────────────────────────────────────────────────
const toggleActive = async (user) => {
  togglingId.value = user.id
  try {
    const response = await useApi(`/api/v1/users/${user.id}/toggle-active`).patch()
    const data = response?.data ?? response
    const idx = users.value.findIndex(u => u.id === user.id)
    if (idx !== -1) users.value[idx].is_active = data.is_active
  } catch (e) {
    console.error('Toggle active error:', e)
  } finally {
    togglingId.value = null
  }
}

// ── Delete ────────────────────────────────────────────────────────────────────
const confirmDelete = (user) => {
  deletingUser.value = user
  deleteDialog.value = true
}

const doDelete = async () => {
  deleting.value = true
  try {
    await useApi(`/api/v1/users/${deletingUser.value.id}`).delete()
    deleteDialog.value = false
    fetchUsers()
  } catch (e) {
    console.error('Delete user error:', e)
  } finally {
    deleting.value = false
  }
}

// ── Helpers ───────────────────────────────────────────────────────────────────
const roleLabel = (user) => user.roles?.[0] ?? '-'

const roleOptions = computed(() =>
  roles.value.map(r => ({ title: r.name, value: r.name })),
)

const hasActiveFilters = computed(() =>
  search.value || filterRole.value || filterActive.value !== null,
)

const clearFilters = () => {
  search.value = ''
  filterRole.value = null
  filterActive.value = null
}

// Table headers
const headers = [
  { title: 'Nama', key: 'name' },
  { title: 'Email', key: 'email' },
  { title: 'Role', key: 'roles', sortable: false },
  { title: 'Status', key: 'is_active', align: 'center', width: '120px' },
  { title: 'Aksi', key: 'actions', align: 'center', sortable: false, width: '180px' },
]
</script>

<template>
  <div>
    <!-- Header -->
    <VRow class="mb-4">
      <VCol cols="12">
        <div class="d-flex align-center justify-space-between flex-wrap gap-3">
          <div>
            <h4 class="text-h4 font-weight-bold">Manajemen User</h4>
            <p class="text-body-2 text-medium-emphasis mb-0">
              Kelola akun dan akses karyawan toko
            </p>
          </div>
          <VBtn
            v-if="ability.can('create', 'User')"
            prepend-icon="ri-user-add-line"
            @click="openCreate"
          >
            Tambah User
          </VBtn>
        </div>
      </VCol>
    </VRow>

    <!-- Filters -->
    <VCard class="mb-4">
      <VCardText>
        <VRow>
          <VCol cols="12" sm="5" md="4">
            <VTextField
              v-model="search"
              placeholder="Cari nama atau email..."
              prepend-inner-icon="ri-search-line"
              clearable
              hide-details
              density="compact"
            />
          </VCol>
          <VCol cols="12" sm="4" md="3">
            <VSelect
              v-model="filterRole"
              :items="roleOptions"
              placeholder="Semua Role"
              clearable
              hide-details
              density="compact"
            />
          </VCol>
          <VCol cols="12" sm="3" md="3">
            <VSelect
              v-model="filterActive"
              :items="activeOptions"
              item-title="title"
              item-value="value"
              hide-details
              density="compact"
            />
          </VCol>
          <VCol cols="12" md="2" class="d-flex align-end">
            <VBtn
              v-if="hasActiveFilters"
              variant="tonal"
              color="secondary"
              size="small"
              icon="ri-filter-off-line"
              @click="clearFilters"
            />
          </VCol>
        </VRow>
      </VCardText>
    </VCard>

    <!-- Table -->
    <VCard>
      <VDataTable
        :headers="headers"
        :items="users"
        :loading="loading"
        :items-per-page="perPage"
        hide-default-footer
      >
        <!-- Nama + avatar -->
        <template #item.name="{ item }">
          <div class="d-flex align-center gap-3 py-1">
            <VAvatar size="36" :color="item.is_active ? 'primary' : 'secondary'" variant="tonal">
              <VImg v-if="item.avatar" :src="item.avatar" />
              <span v-else class="text-body-2 font-weight-bold">
                {{ item.name.charAt(0).toUpperCase() }}
              </span>
            </VAvatar>
            <div>
              <p class="font-weight-medium mb-0">{{ item.name }}</p>
              <p class="text-body-2 text-medium-emphasis mb-0">{{ item.email }}</p>
              <p v-if="item.phone" class="text-body-2 text-medium-emphasis mb-0">
                <VIcon icon="ri-phone-line" size="12" class="me-1" />{{ item.phone }}
              </p>
            </div>
          </div>
        </template>

        <!-- Email (hidden pada mobile — sudah ada di nama) -->
        <template #item.email>
          <!-- ditampilkan di nama cell -->
        </template>

        <!-- Role -->
        <template #item.roles="{ item }">
          <VChip
            v-if="item.roles?.length"
            color="primary"
            variant="tonal"
            size="small"
            label
          >
            {{ roleLabel(item) }}
          </VChip>
          <span v-else class="text-medium-emphasis">-</span>
        </template>

        <!-- Status -->
        <template #item.is_active="{ item }">
          <VSwitch
            :model-value="item.is_active"
            :loading="togglingId === item.id"
            color="success"
            hide-details
            density="compact"
            :disabled="!ability.can('update', 'User')"
            @update:model-value="toggleActive(item)"
          />
        </template>

        <!-- Aksi -->
        <template #item.actions="{ item }">
          <VBtn
            v-if="ability.can('manage', 'Attendance')"
            icon="ri-fingerprint-line"
            variant="text"
            size="small"
            color="secondary"
            @click="openAttendanceDetail(item)"
          >
            <VIcon icon="ri-fingerprint-line" />
            <VTooltip activator="parent">Detail Absensi</VTooltip>
          </VBtn>
          <VBtn
            v-if="ability.can('update', 'User')"
            icon="ri-pencil-line"
            variant="text"
            size="small"
            color="primary"
            @click="openEdit(item)"
          />
          <VBtn
            v-if="ability.can('delete', 'User')"
            icon="ri-delete-bin-line"
            variant="text"
            size="small"
            color="error"
            @click="confirmDelete(item)"
          />
        </template>

        <!-- Empty state -->
        <template #no-data>
          <div class="text-center py-10">
            <VIcon icon="ri-team-line" size="64" color="secondary" class="mb-4" />
            <p class="text-h6 text-medium-emphasis">Belum ada user</p>
            <p class="text-body-2 text-medium-emphasis">
              {{ hasActiveFilters ? 'Coba ubah filter pencarian' : 'Klik "Tambah User" untuk mulai' }}
            </p>
          </div>
        </template>
      </VDataTable>

      <!-- Pagination -->
      <VDivider />
      <VCardText class="d-flex align-center justify-space-between flex-wrap gap-3 pa-4">
        <span class="text-body-2 text-medium-emphasis">Total: {{ totalItems }} user</span>
        <VPagination
          v-if="totalItems > perPage"
          v-model="page"
          :length="Math.ceil(totalItems / perPage)"
          :total-visible="5"
          density="compact"
        />
      </VCardText>
    </VCard>

    <!-- ── Create / Edit Dialog ─────────────────────────────────────────────── -->
    <VDialog v-model="dialog" max-width="520" persistent>
      <VCard>
        <VCardTitle class="pa-6 pb-0">{{ dialogTitle }}</VCardTitle>
        <VCardText class="pa-6">
          <VRow>
            <VCol cols="12">
              <VTextField
                v-model="form.name"
                label="Nama Lengkap *"
                placeholder="cth. Budi Santoso"
                :error-messages="errors.name"
                autofocus
              />
            </VCol>
            <VCol cols="12">
              <VTextField
                v-model="form.email"
                label="Email *"
                type="email"
                placeholder="budi@kopisenja.com"
                :error-messages="errors.email"
              />
            </VCol>
            <VCol cols="12">
              <VTextField
                v-model="form.phone"
                label="No. HP (opsional)"
                placeholder="08123456789"
                :error-messages="errors.phone"
              />
            </VCol>
            <VCol cols="12">
              <VTextField
                v-model="form.password"
                :label="editingId ? 'Password Baru (kosongkan jika tidak diubah)' : 'Password *'"
                :type="showPassword ? 'text' : 'password'"
                placeholder="Min. 8 karakter"
                :error-messages="errors.password"
                :append-inner-icon="showPassword ? 'ri-eye-off-line' : 'ri-eye-line'"
                @click:append-inner="showPassword = !showPassword"
              />
            </VCol>
            <VCol cols="12" sm="6">
              <VSelect
                v-model="form.role"
                :items="roleOptions"
                label="Role *"
                placeholder="Pilih role"
                :error-messages="errors.role"
              />
            </VCol>
            <VCol cols="12" sm="6" class="d-flex align-center">
              <VSwitch
                v-model="form.is_active"
                label="Akun Aktif"
                color="success"
                hide-details
              />
            </VCol>
          </VRow>
        </VCardText>
        <VCardActions class="pa-6 pt-0">
          <VSpacer />
          <VBtn variant="tonal" color="secondary" :disabled="saving" @click="dialog = false">
            Batal
          </VBtn>
          <VBtn color="primary" :loading="saving" @click="save">
            Simpan
          </VBtn>
        </VCardActions>
      </VCard>
    </VDialog>

    <!-- ── Delete Dialog ────────────────────────────────────────────────────── -->
    <VDialog v-model="deleteDialog" max-width="420">
      <VCard>
        <VCardTitle class="pa-6 pb-2">Hapus User?</VCardTitle>
        <VCardText class="pa-6 pt-0">
          Akun <strong>{{ deletingUser?.name }}</strong> akan dihapus permanen.
          Aksi ini tidak dapat dibatalkan.
        </VCardText>
        <VCardActions class="pa-6 pt-0">
          <VSpacer />
          <VBtn variant="tonal" color="secondary" :disabled="deleting" @click="deleteDialog = false">
            Batal
          </VBtn>
          <VBtn color="error" :loading="deleting" @click="doDelete">
            Hapus
          </VBtn>
        </VCardActions>
      </VCard>
    </VDialog>

    <!-- ── Detail Absensi (PIN & QR) Dialog ─────────────────────────────────── -->
    <StaffAttendanceCredentialDialog
      v-model="attendanceDialog"
      :user="attendanceUser"
    />
  </div>
</template>
